import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
    Terminal, Globe, Zap, Cpu, Shield, Cloud, Settings, Layers,
    CheckCircle2, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import './ServicesPage.css';

const categoriesData = [
    {
        id: "digital-products",
        number: "01",
        title: "Digital Products",
        description: "Engaging user experiences designed and built to help your business establish a powerful digital presence.",
        services: [
            {
                icon: <Globe size={24} className="service-card-icon" />,
                name: "Web Development",
                desc: "Modern, responsive websites and web applications designed to engage users and maximize conversions.",
                features: ["Next.js & React Frameworks", "SEO & Core Web Vitals Optimized", "Headless CMS Integration", "Sub-second Load Speeds"]
            },
            {
                icon: <Zap size={24} className="service-card-icon" />,
                name: "Mobile Applications",
                desc: "Native and cross-platform mobile experiences designed to put your business directly in your users' hands.",
                features: ["iOS & Android Native Apps", "React Native & Flutter", "Offline Synchronization", "Seamless App Store Deployment"]
            }
        ]
    },
    {
        id: "software-business-systems",
        number: "02",
        title: "Software & Business Systems",
        description: "High-performance software systems built to solve operational bottlenecks and automate critical workflows.",
        services: [
            {
                icon: <Terminal size={24} className="service-card-icon" />,
                name: "Software Engineering",
                desc: "Custom backend architectures, modular monoliths, and microservices engineered for absolute reliability.",
                features: ["Node.js, Go & Python backends", "High-concurrency processing", "Clean, maintainable codebases", "Scalable Database Design"]
            },
            {
                icon: <Settings size={24} className="service-card-icon" />,
                name: "Systems Integration",
                desc: "Secure data pipelines and custom middleware connecting your CRM, ERP, and third-party tools seamlessly.",
                features: ["Custom API development", "ERP & CRM integrations", "Legacy systems connectivity", "Unified Single Source of Truth"]
            },
            {
                icon: <Layers size={24} className="service-card-icon" />,
                name: "Enterprise Architecture",
                desc: "Technical blueprinting and scalable patterns designed to support complex, growing organizational structures.",
                features: ["Zero downtime failovers", "Microservices modular patterns", "Enterprise standards compliance", "Long-term scalability planning"]
            }
        ]
    },
    {
        id: "intelligent-transformation",
        number: "03",
        title: "Intelligent & Digital Transformation",
        description: "Adopting advanced technology workflows to scale operational capacity and modernize your customer touchpoints.",
        services: [
            {
                icon: <Cpu size={24} className="service-card-icon" />,
                name: "AI & Automation",
                desc: "Intelligent LLM agents, automated scripts, and robotic process automation that eliminate repetitive work.",
                features: ["Custom LLMs & Chatbots", "Robotic Process Automation (RPA)", "Automated business workflows", "Predictive Analytics integration"]
            },
            {
                icon: <Globe size={24} className="service-card-icon" />,
                name: "Digital Transformation",
                desc: "Strategic modernization of core technology systems to improve team agility and end-user satisfaction.",
                features: ["Legacy infrastructure modernization", "Technology stack audit", "Operational workflow optimization", "Unified customer experience mapping"]
            }
        ]
    },
    {
        id: "infrastructure-security",
        number: "04",
        title: "Infrastructure & Security",
        description: "Hardened environments and scalable cloud architectures designed to host and protect your enterprise data.",
        services: [
            {
                icon: <Cloud size={24} className="service-card-icon" />,
                name: "Cloud Infrastructure",
                desc: "Cost-optimized, highly available deployment environments built utilizing modern DevOps paradigms.",
                features: ["AWS, GCP & Azure deployments", "Infrastructure-as-Code (Terraform)", "Docker & Kubernetes setup", "Disaster recovery planning"]
            },
            {
                icon: <Shield size={24} className="service-card-icon" />,
                name: "Cybersecurity",
                desc: "Proactive assessment, zero-trust architectures, and hard security configurations to safeguard operations.",
                features: ["Vulnerability assessments", "Zero-Trust Architecture setup", "Secure data encryption standards", "Real-time threat monitoring configurations"]
            }
        ]
    }
];

const ServicesPage = () => {
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [location]);

    return (
        <div className="services-page">
            <Helmet>
                <title>Our Services | Kodebuild | Professional Software Engineering</title>
                <meta name="description" content="Explore Kodebuild's structured strategic services: digital products, enterprise business software, intelligent automation, cloud architectures, and cybersecurity." />
                <link rel="canonical" href="https://kodebuildtech.com/services" />
                {/* Open Graph */}
                <meta property="og:title" content="Our Services | Kodebuild | Professional Software Engineering" />
                <meta property="og:description" content="Web development, mobile apps, AI automation, cloud infrastructure, and cybersecurity — premium engineering services from Kodebuild." />
                <meta property="og:url" content="https://kodebuildtech.com/services" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Our Services | Kodebuild | Professional Software Engineering" />
                <meta name="twitter:description" content="Web development, mobile apps, AI automation, cloud infrastructure, and cybersecurity." />
                <meta name="twitter:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
            </Helmet>

            {/* Services Hero */}
            <section className="services-hero">
                <div className="services-hero-glow" />
                <div className="services-hero-glow-2" />
                <div className="container">
                    <motion.span
                        className="section-badge"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Capabilities
                    </motion.span>
                    <motion.h1
                        className="services-hero-title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        Our <span className="text-gradient">Services</span>
                    </motion.h1>
                    <motion.p
                        className="services-hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Technology solutions designed around your business. We partition our engineering capabilities into four strategic areas built for growth.
                    </motion.p>
                </div>
            </section>

            {/* Service Categories Sections */}
            <div className="services-categories-container">
                {categoriesData.map((category, index) => (
                    <section
                        key={category.id}
                        id={category.id}
                        className={`services-category-section ${index % 2 === 1 ? 'alt-bg' : ''}`}
                    >
                        <div className="container">
                            <div className="category-header-block">
                                <div className="category-meta">
                                    <span className="category-num">{category.number}</span>
                                    <h2 className="category-title">{category.title}</h2>
                                </div>
                                <p className="category-desc">{category.description}</p>
                            </div>

                            <div className="services-list-grid">
                                {category.services.map((service, sIndex) => (
                                    <motion.div
                                        key={sIndex}
                                        className="services-detail-card glass"
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.6, delay: sIndex * 0.1 }}
                                    >
                                        <div className="service-card-header">
                                            <div className="service-icon-wrapper">
                                                {service.icon}
                                            </div>
                                            <h3 className="service-card-name">{service.name}</h3>
                                        </div>
                                        <p className="service-card-desc">{service.desc}</p>
                                        
                                        <div className="service-features-list">
                                            {service.features.map((feature, fIndex) => (
                                                <div key={fIndex} className="service-feature-bullet">
                                                    <CheckCircle2 size={16} className="bullet-check" />
                                                    <span>{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            {/* CTA Section */}
            <section className="services-cta-section">
                <div className="container">
                    <div className="services-cta-card glass">
                        <h2>Ready to transform your business software?</h2>
                        <p>Let's map out your technology requirements and build a high-performance system for your organization.</p>
                        <Link to="/contact" className="btn-primary glow-hover">
                            Start a Project <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;
